import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../store/slices/usersSlice';
import { fetchRolePermissions, saveRolePermissions } from '../../store/slices/permissionsSlice';
import { fetchLocations } from '../../store/slices/locationsSlice';
import { dedupeLocations } from '../../utils/locations';

const ROLE_CONFIG = {
  ADMIN:            { label: 'Admin',            bg: 'bg-brand-100',   text: 'text-brand-600',   dot: 'bg-brand-500'   },
  RE:               { label: 'Resident Engineer', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  TEAM_LEAD:        { label: 'Team Lead',         bg: 'bg-sky-100',     text: 'text-sky-700',     dot: 'bg-sky-500'     },
  BHEL_COORDINATOR: { label: 'BHEL Coordinator',  bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  MANAGER:          { label: 'Manager',           bg: 'bg-violet-100',  text: 'text-violet-700',  dot: 'bg-violet-500'  },
  SUPPLY_CHAIN:     { label: 'Supply Chain',      bg: 'bg-teal-100',    text: 'text-teal-700',    dot: 'bg-teal-500'    },
};

const PERMISSION_GROUPS = [
  { group: 'Tickets', items: [
    { key: 'view_all_tickets',  label: 'View All Tickets',          desc: 'See tickets from all locations' },
    { key: 'view_own_tickets',  label: 'View Own Location Tickets', desc: 'Tickets for their assigned location only' },
    { key: 'create_ticket',     label: 'Create Ticket',             desc: 'Raise a new incident' },
    { key: 'transition_ticket', label: 'Move Ticket Status',        desc: 'Move tickets through workflow states' },
    { key: 'signoff_ticket',    label: 'Sign Off Ticket',           desc: 'BHEL approval / sign-off action' },
  ]},
  { group: 'Navigation & Pages', items: [
    { key: 'view_dashboard', label: 'Dashboard',       desc: 'View the main dashboard' },
    { key: 'view_agents',    label: 'Agents Page',     desc: 'See agent availability status' },
    { key: 'manage_users',   label: 'User Management', desc: 'Add, edit, remove users and configure roles' },
  ]},
  { group: 'Attendance', items: [
    { key: 'view_attendance', label: 'View Attendance', desc: 'See attendance records' },
    { key: 'mark_attendance', label: 'Mark Attendance', desc: 'Log daily RE attendance' },
  ]},
];

const ROLE_COLS = [
  { key: 'ADMIN',            label: 'Admin',             color: 'text-brand-600',   bg: 'bg-brand-50'   },
  { key: 'RE',               label: 'Resident Engineer', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  { key: 'TEAM_LEAD',        label: 'Team Lead',         color: 'text-sky-700',     bg: 'bg-sky-50'     },
  { key: 'BHEL_COORDINATOR', label: 'BHEL Coordinator',  color: 'text-amber-700',   bg: 'bg-amber-50'   },
  { key: 'MANAGER',          label: 'Manager',           color: 'text-violet-700',  bg: 'bg-violet-50'  },
  { key: 'SUPPLY_CHAIN',     label: 'Supply Chain',      color: 'text-teal-700',    bg: 'bg-teal-50'    },
];

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || { label: role, bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function Avatar({ name, role }) {
  const initials = (name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const colors = { ADMIN: 'bg-brand-500', RE: 'bg-emerald-500', TEAM_LEAD: 'bg-sky-500', BHEL_COORDINATOR: 'bg-amber-500', MANAGER: 'bg-violet-500', SUPPLY_CHAIN: 'bg-teal-500' };
  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${colors[role] || 'bg-gray-400'}`}>
      {initials}
    </div>
  );
}

function ToggleSwitch({ checked, disabled, onChange }) {
  return (
    <button type="button" onClick={onChange} disabled={disabled}
      className={`relative inline-flex w-10 h-5 rounded-full transition-colors focus:outline-none ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'bg-emerald-500' : 'bg-gray-200'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

const EMPTY_FORM = { full_name: '', email: '', role: 'RE', designation: 'RE', employee_id: '', phone: '', sub_location: '', location_ids: [], team_lead: '', password: '', is_active: true };

export default function UsersPage() {
  const dispatch = useDispatch();
  const { list: users, loading } = useSelector(s => s.users);
  const { matrix: savedMatrix, saving: permSaving } = useSelector(s => s.permissions);
  const { list: allLocations } = useSelector(s => s.locations);
  const currentUser = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const locationOptions = dedupeLocations(allLocations);

  const [activeTab, setActiveTab] = useState('users');
  const [filterRole, setFilterRole] = useState('ALL');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Permissions tab state
  const [localMatrix, setLocalMatrix] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => { dispatch(fetchUsers()); dispatch(fetchLocations()); }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'permissions') dispatch(fetchRolePermissions());
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (Object.keys(savedMatrix).length) {
      setLocalMatrix(JSON.parse(JSON.stringify(savedMatrix)));
      setIsDirty(false);
    }
  }, [savedMatrix]);

  const togglePerm = (role, key) => {
    if (role === 'ADMIN') return;
    setLocalMatrix(prev => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [key]: !(prev[role]?.[key]) }
    }));
    setIsDirty(true);
  };

  const handlePermSave = async () => {
    await dispatch(saveRolePermissions(localMatrix));
    setIsDirty(false);
  };

  const filtered = users.filter(u => {
    const matchRole = filterRole === 'ALL' || u.role === filterRole;
    const matchSearch = !search || u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSearch;
  });

  const openCreate = () => { setForm(EMPTY_FORM); setFormError(null); setModal('create'); };
  const openEdit = (u) => { setForm({ full_name: u.full_name, email: u.email, role: u.role, designation: u.designation || 'RE', employee_id: u.employee_id || '', phone: u.phone || '', sub_location: u.sub_location || '', location_ids: (u.locations_data || []).map(l => l.id), team_lead: u.team_lead ? String(u.team_lead) : '', password: '', is_active: u.is_active }); setFormError(null); setModal(u); };

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      let result;
      if (modal === 'create') {
        const payload = { full_name: form.full_name, email: form.email, role: form.role, designation: form.designation, employee_id: form.employee_id, phone: form.phone, sub_location: form.sub_location, location_ids: form.location_ids, location: form.location_ids[0] || null, team_lead: form.team_lead ? parseInt(form.team_lead) : null, password: form.password || 'Agent@123' };
        result = await dispatch(createUser(payload));
        if (createUser.rejected.match(result)) { setFormError(result.payload); setSaving(false); return; }
      } else {
        const payload = { full_name: form.full_name, role: form.role, designation: form.designation, employee_id: form.employee_id, phone: form.phone, sub_location: form.sub_location, location_ids: form.location_ids, location: form.location_ids[0] || null, team_lead: form.team_lead ? parseInt(form.team_lead) : null, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        result = await dispatch(updateUser({ id: modal.id, ...payload }));
        if (updateUser.rejected.match(result)) { setFormError(result.payload); setSaving(false); return; }
      }
      setModal(null);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await dispatch(deleteUser(id));
    setConfirmDelete(null);
  };

  const counts = Object.keys(ROLE_CONFIG).reduce((acc, r) => {
    acc[r] = users.filter(u => u.role === r).length;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">User Management</h1>
          <p className="text-gray-400 text-sm mt-0.5">{users.length} users · {Object.keys(ROLE_CONFIG).length} roles</p>
        </div>
        {activeTab === 'users' && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add User
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-brand-50 p-1 rounded-xl w-fit">
        {[['users', 'Users'], ['permissions', 'Role Permissions']].map(([id, lbl]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === id ? 'bg-white shadow-sm text-brand-500' : 'text-gray-400 hover:text-brand-400'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {activeTab === 'users' ? (
        <>
          {/* Role stat pills */}
          <div className="flex gap-3 flex-wrap">
            {[['ALL', 'All Users', users.length, 'bg-brand-50 border-brand-200 text-brand-600'], ...Object.entries(ROLE_CONFIG).map(([k, v]) => [k, v.label, counts[k] || 0, `${v.bg} border-transparent ${v.text}`])].map(([role, label, count, cls]) => (
              <button key={role} onClick={() => setFilterRole(role)}
                className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-all ${filterRole === role ? 'ring-2 ring-offset-1 ring-magenta-400 ' + cls : cls + ' opacity-60 hover:opacity-100'}`}>
                {label} <span className="ml-1 font-bold">{count}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…" className="input-field pl-9" />
          </div>

          {/* User cards grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <svg className="animate-spin w-8 h-8 text-magenta-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {filtered.map(u => (
                <div key={u.id} className={`card p-5 flex flex-col gap-3 ${!u.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar name={u.full_name} role={u.role} />
                      <div className="min-w-0">
                        <p className="font-bold text-brand-600 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-400 truncate">{u.email}</p>
                      </div>
                    </div>
                    {!u.is_active && (
                      <span className="text-xs bg-gray-100 text-gray-500 font-semibold px-2 py-0.5 rounded-full flex-shrink-0">Inactive</span>
                    )}
                  </div>
                  {/* Employee ID — prominent */}
                  {u.employee_id && (
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-brand-600 bg-brand-50 border border-brand-200 px-2.5 py-1 rounded-lg tracking-wider">{u.employee_id}</span>
                      {u.designation === 'TL' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">Team Lead</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    <RoleBadge role={u.role} />
                    {!u.employee_id && u.designation === 'TL' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">Team Lead</span>
                    )}
                  </div>

                  {/* Locations */}
                  {u.locations_data && u.locations_data.length > 0 ? (
                    <div className="flex items-start gap-1.5">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                      <div className="flex flex-wrap gap-1">
                        {u.locations_data.map(l => (
                          <span key={l.id} className="text-xs bg-brand-50 border border-brand-100 text-brand-600 font-medium px-1.5 py-0.5 rounded-md">{l.name}</span>
                        ))}
                      </div>
                    </div>
                  ) : u.location_name ? (
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                      <span className="truncate font-medium">{u.location_name}</span>
                    </div>
                  ) : null}
                  {u.sub_location && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 pl-5">
                      <span className="truncate">{u.sub_location}</span>
                    </div>
                  )}

                  {/* Team lead / team members */}
                  {u.role === 'RE' && u.team_lead_name && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                      <span>Reports to <span className="font-semibold text-sky-600">{u.team_lead_name}</span></span>
                    </div>
                  )}
                  {u.role === 'TEAM_LEAD' && u.team_members_count != null && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                      <span><span className="font-semibold text-sky-600">{u.team_members_count}</span> engineer{u.team_members_count !== 1 ? 's' : ''} reporting</span>
                    </div>
                  )}

                  {/* Joining Date */}
                  {(u.date_of_joining || u.created_at) && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                      <span>
                        Joined{' '}
                        <span className="font-semibold text-gray-700">
                          {new Date(u.date_of_joining || u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </span>
                    </div>
                  )}

                  {u.phone && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                      <a href={`tel:${u.phone}`} className="hover:text-brand-500 hover:underline">{u.phone}</a>
                    </div>
                  )}
                  <div className="flex gap-2 pt-1 border-t border-brand-50">
                    <button onClick={() => openEdit(u)}
                      className="flex-1 text-xs font-semibold text-brand-500 hover:text-magenta-500 flex items-center justify-center gap-1 py-1.5 rounded-lg hover:bg-brand-50 transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      Edit
                    </button>
                    {u.id !== currentUser.id && (
                      <button onClick={() => setConfirmDelete(u)}
                        className="flex-1 text-xs font-semibold text-red-400 hover:text-red-600 flex items-center justify-center gap-1 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-3 text-center py-16 text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <p className="font-semibold">No users found</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── Permissions tab ── */
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">Control what each role can see and do in the application.</p>
            <div className="flex items-center gap-3">
              {isDirty && (
                <span className="text-xs text-amber-600 font-semibold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                  Unsaved changes
                </span>
              )}
              <button
                onClick={() => { setLocalMatrix(JSON.parse(JSON.stringify(savedMatrix))); setIsDirty(false); }}
                disabled={!isDirty}
                className="btn-secondary text-sm disabled:opacity-40">
                Reset
              </button>
              <button onClick={handlePermSave} disabled={!isDirty || permSaving}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
                {permSaving && <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>}
                Save Changes
              </button>
            </div>
          </div>

          {/* Matrix table */}
          <div className="card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left px-5 py-3.5 text-sm font-semibold text-gray-500 border-b border-brand-50 w-80">Permission</th>
                  {ROLE_COLS.map(r => (
                    <th key={r.key} className={`px-4 py-3.5 text-center text-sm font-bold border-b border-brand-50 ${r.color} ${r.bg}`}>
                      {r.label}
                      {r.key === 'ADMIN' && <div className="text-xs font-normal text-gray-400 mt-0.5">Always full access</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map((group) => (
                  <React.Fragment key={group.group}>
                    <tr>
                      <td colSpan={5} className="px-5 py-2 bg-gray-50 text-xs font-bold text-gray-400 uppercase tracking-wider border-y border-gray-100">
                        {group.group}
                      </td>
                    </tr>
                    {group.items.map((item, idx) => (
                      <tr key={item.key} className={`hover:bg-gray-50/50 transition-colors ${idx < group.items.length - 1 ? 'border-b border-gray-100' : ''}`}>
                        <td className="px-5 py-4">
                          <p className="text-sm font-medium text-gray-700">{item.label}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                        </td>
                        {ROLE_COLS.map(r => (
                          <td key={r.key} className="px-4 py-4 text-center">
                            <div className="flex justify-center">
                              <ToggleSwitch
                                checked={r.key === 'ADMIN' ? true : (localMatrix[r.key]?.[item.key] ?? false)}
                                disabled={r.key === 'ADMIN'}
                                onChange={() => togglePerm(r.key, item.key)}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-brand-50 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="font-bold text-brand-500 text-lg">{modal === 'create' ? 'Add New User' : `Edit — ${modal.full_name}`}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{modal === 'create' ? 'Create a new account' : 'Update user details or role'}</p>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  {typeof formError === 'object' ? Object.entries(formError).map(([k, v]) => <p key={k}><strong>{k}:</strong> {Array.isArray(v) ? v.join(', ') : v}</p>) : formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-magenta-500">*</span></label>
                <input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} className="input-field" placeholder="e.g. Rohit Maurya" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-magenta-500">*</span></label>
                <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="input-field" placeholder="name@domain.com" disabled={modal !== 'create'} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  className="input-field" placeholder={modal === 'create' ? 'Leave blank for default: Agent@123' : 'Leave blank to keep current password'} />
                <p className="text-xs text-gray-400 mt-1">Minimum 8 characters if set.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Role <span className="text-magenta-500">*</span></label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="input-field">
                  {Object.entries(ROLE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              {form.role === 'RE' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Reports To (Team Lead)</label>
                  <select value={form.team_lead} onChange={e => setForm(p => ({ ...p, team_lead: e.target.value }))} className="input-field">
                    <option value="">— No Team Lead —</option>
                    {users.filter(u => u.role === 'TEAM_LEAD' && u.is_active).map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}{u.employee_id ? ` (${u.employee_id})` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Employee ID</label>
                <input value={form.employee_id} onChange={e => setForm(p => ({ ...p, employee_id: e.target.value }))} className="input-field" placeholder="e.g. R076" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="input-field" placeholder="Mobile number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Assigned Locations
                  {form.location_ids.length > 0 && <span className="ml-2 text-xs text-brand-500 font-semibold">{form.location_ids.length} selected</span>}
                </label>
                <div className="border border-gray-200 rounded-xl max-h-44 overflow-y-auto divide-y divide-gray-50">
                  {locationOptions.map(loc => {
                    const checked = form.location_ids.includes(loc.id);
                    return (
                      <label key={loc.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-brand-50/50 transition-colors ${checked ? 'bg-brand-50' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm(p => ({ ...p, location_ids: checked ? p.location_ids.filter(id => id !== loc.id) : [...p.location_ids, loc.id] }))}
                          className="w-4 h-4 accent-brand-500 rounded"
                        />
                        <span className={`text-sm truncate ${checked ? 'font-semibold text-brand-700' : 'text-gray-600'}`}>{loc.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {modal !== 'create' && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-10 h-6 rounded-full transition-colors relative ${form.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{form.is_active ? 'Active' : 'Inactive'}</span>
                </label>
              )}
            </div>
            <div className="px-6 py-4 border-t border-brand-50 flex gap-3">
              <button onClick={() => setModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.full_name || !form.email}
                className="btn-primary flex-1 justify-center flex items-center gap-2 disabled:opacity-50">
                {saving && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>}
                {modal === 'create' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-1">Remove User</h3>
            <p className="text-gray-500 text-sm mb-5">Are you sure you want to remove <strong>{confirmDelete.full_name}</strong>? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
